import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { listProjectResale } from "../../Redux/Actions/projectResaleActions";
import { assignProject } from "../../Redux/Actions/propertiesResaleActions";
import toast from "react-hot-toast";

const statusStyle = (status) => {
    const map = {
        published: { background: "#EAF3DE", color: "#3B6D11" },
        approved: { background: "#E6F1FB", color: "#185FA5" },
        unpublished: { background: "#F1EFE8", color: "#5F5E5A" },
        requested: { background: "#FAEEDA", color: "#854F0B" },
        declined: { background: "#FCEBEB", color: "#A32D2D" },
    };
    return map[status?.toLowerCase()] || map.unpublished;
};

const AssignProjectPopup = ({ isOpen, onClose, propertyId, onAssigned, currentProject }) => {
    const dispatch = useDispatch();

    const [page, setPage] = useState(1);
    const [projects, setProjects] = useState([]);
    const [total, setTotal] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedProject, setSelectedProject] = useState(null);
    const [saving, setSaving] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);

    const LIMIT = 10;

    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 500);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        if (!isOpen) return;
        setPage(1);
        setProjects([]);
        setSelectedProject(currentProject || null);
        setSearch("");
        fetchProjects(1, true);
    }, [isOpen, debouncedSearch]);

    const fetchProjects = useCallback(async (pageNum, isReset = false) => {
        try {
            isReset ? setInitialLoading(true) : setLoadingMore(true);
            const res = await dispatch(
                listProjectResale({
                    page: pageNum,
                    limit: LIMIT,
                    is_deleted: false,
                    business_type: "resale",
                    filter: debouncedSearch,
                    status: ["published", "approved", "unpublished"],
                }, false)
            );
            if (res?.data) {
                setProjects((prev) => isReset ? res.data : [...prev, ...res.data]);
                setTotal(res.total || 0);
            }
        } catch {
            toast.error("Failed to load projects");
        } finally {
            setInitialLoading(false);
            setLoadingMore(false);
        }
    }, [dispatch, debouncedSearch]);

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        if (scrollHeight - scrollTop <= clientHeight + 20 && !loadingMore && projects.length < total) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchProjects(nextPage);
        }
    };

    const handleAssign = async () => {
        if (!selectedProject) return toast.error("Please select a project");
        try {
            setSaving(true);
            const res = await dispatch(assignProject({
                property: propertyId,
                project: selectedProject._id,
            }));
            if (res?.statusCode === 200) {
                toast.success("Project assigned successfully");
                onAssigned?.();
                onClose();
            }
        } catch {
            toast.error("Failed to assign project");
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: "fixed", inset: 0, zIndex: 9999,
            background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center",
            justifyContent: "center", padding: "16px",
        }}>
            <div style={{
                background: "#fff",
                borderRadius: 16,
                width: "100%",
                maxWidth: 520,
                maxHeight: "88vh",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                overflow: "hidden",
            }}>

                {/* ── Header ── */}
                <div style={{
                    padding: "20px 24px 16px",
                    borderBottom: "1px solid #f3f4f6",
                    display: "flex", alignItems: "flex-start",
                    justifyContent: "space-between", flexShrink: 0,
                }}>
                    <div>
                        <p style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#111827" }}>
                            Assign Project
                        </p>
                        <p style={{ margin: "3px 0 0", fontSize: 13, color: "#6b7280" }}>
                            Select a project to link to this property
                        </p>
                    </div>
                    <button onClick={onClose} style={{
                        width: 32, height: 32, borderRadius: "50%",
                        border: "1px solid #e5e7eb",
                        background: "#f9fafb",
                        cursor: "pointer", display: "flex",
                        alignItems: "center", justifyContent: "center",
                        fontSize: 16, color: "#6b7280", flexShrink: 0,
                        marginLeft: 12,
                    }}>✕</button>
                </div>

                {/* ── Search ── */}
                <div style={{ padding: "16px 24px 0", flexShrink: 0 }}>
                    <div style={{ position: "relative" }}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                            stroke="#9ca3af" strokeWidth="2"
                            style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}>
                            <circle cx="11" cy="11" r="8" />
                            <line x1="21" y1="21" x2="16.65" y2="16.65" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Search by project name or ID..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                                width: "100%", padding: "10px 12px 10px 38px",
                                border: "1px solid #e5e7eb", borderRadius: 10,
                                fontSize: 13, outline: "none",
                                background: "#f9fafb", color: "#111827",
                                boxSizing: "border-box",
                            }}
                        />
                    </div>
                </div>

                {/* ── Selected Preview ── */}
                {selectedProject && (
                    <div style={{ padding: "12px 24px 0", flexShrink: 0 }}>
                        <div style={{
                            background: "#f0fdf4", border: "1px solid #bbf7d0",
                            borderRadius: 10, padding: "10px 14px",
                            display: "flex", alignItems: "center",
                            justifyContent: "space-between", gap: 10,
                        }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div style={{
                                    width: 28, height: 28, borderRadius: "50%",
                                    background: "#16a34a", display: "flex",
                                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                                }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                                        <polyline points="20 6 9 17 4 12" />
                                    </svg>
                                </div>
                                <div>
                                    <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#15803d" }}>
                                        {selectedProject.project_name}
                                    </p>
                                    <p style={{ margin: 0, fontSize: 11, color: "#16a34a" }}>
                                        {selectedProject.project_id}
                                        {selectedProject.city?.city_name && ` · ${selectedProject.city.city_name}`}
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => setSelectedProject(null)} style={{
                                background: "none", border: "none",
                                cursor: "pointer", color: "#16a34a", fontSize: 16, lineHeight: 1,
                            }}>✕</button>
                        </div>
                    </div>
                )}

                {/* ── Count label ── */}
                {!initialLoading && projects.length > 0 && (
                    <div style={{ padding: "12px 24px 4px", flexShrink: 0 }}>
                        <p style={{ margin: 0, fontSize: 11, color: "#9ca3af", letterSpacing: "0.4px" }}>
                            {total} PROJECT{total !== 1 ? "S" : ""}
                        </p>
                    </div>
                )}

                {/* ── List ── */}
                <div
                    onScroll={handleScroll}
                    style={{
                        flex: 1, overflowY: "auto",
                        margin: "8px 24px 0",
                        border: "1px solid #f3f4f6",
                        borderRadius: 12,
                        minHeight: 160,
                    }}
                >
                    {initialLoading ? (
                        <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "#9ca3af" }}>
                            Loading projects...
                        </div>
                    ) : projects.length === 0 ? (
                        <div style={{ padding: 24, textAlign: "center", fontSize: 13, color: "#9ca3af" }}>
                            No projects found.
                        </div>
                    ) : (
                        <>
                            {projects.map((item, idx) => {
                                const isSelected = selectedProject?._id === item._id;
                                const ss = statusStyle(item.status);
                                return (
                                    <div
                                        key={item._id}
                                        onClick={() => setSelectedProject(isSelected ? null : item)}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 12,
                                            padding: "12px 14px",
                                            borderBottom: idx < projects.length - 1 ? "1px solid #f9fafb" : "none",
                                            cursor: "pointer",
                                            background: isSelected ? "#f0fdf4" : "#fff",
                                            transition: "background 0.12s",
                                        }}
                                    >
                                        {/* Radio */}
                                        <div style={{
                                            width: 18, height: 18, borderRadius: "50%",
                                            border: `2px solid ${isSelected ? "#16a34a" : "#d1d5db"}`,
                                            background: isSelected ? "#16a34a" : "#fff",
                                            display: "flex", alignItems: "center",
                                            justifyContent: "center", flexShrink: 0,
                                            transition: "all 0.12s",
                                        }}>
                                            {isSelected && (
                                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5">
                                                    <polyline points="20 6 9 17 4 12" />
                                                </svg>
                                            )}
                                        </div>

                                        {/* Info */}
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <p style={{
                                                margin: 0, fontSize: 13, fontWeight: 600,
                                                color: "#111827", whiteSpace: "nowrap",
                                                overflow: "hidden", textOverflow: "ellipsis",
                                            }}>
                                                {item.project_name}
                                            </p>
                                            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#6b7280" }}>
                                                {item.project_id}
                                                {item.city?.city_name && ` · ${item.city.city_name}`}
                                                {item.builder?.name && ` · ${item.builder.name}`}
                                            </p>
                                        </div>

                                        {/* Status */}
                                        {item.status && (
                                            <span style={{
                                                fontSize: 11, padding: "3px 9px",
                                                borderRadius: 999, flexShrink: 0,
                                                background: ss.background,
                                                color: ss.color,
                                                fontWeight: 500,
                                                textTransform: "capitalize",
                                            }}>
                                                {item.status}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}

                            {loadingMore && (
                                <div style={{ padding: "10px", textAlign: "center", fontSize: 12, color: "#9ca3af" }}>
                                    Loading more...
                                </div>
                            )}

                            {!loadingMore && projects.length >= total && (
                                <div style={{
                                    padding: "10px", textAlign: "center",
                                    fontSize: 11, color: "#d1d5db",
                                    borderTop: "1px solid #f9fafb",
                                }}>
                                    All {total} projects loaded
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* ── Footer ── */}
                <div style={{
                    padding: "14px 24px 20px",
                    borderTop: "1px solid #f3f4f6",
                    display: "flex", gap: 10, flexShrink: 0, marginTop: 12,
                }}>
                    <button onClick={onClose} style={{
                        flex: 1, padding: "10px 14px",
                        borderRadius: 10, border: "1px solid #e5e7eb",
                        background: "#fff", fontSize: 13,
                        fontWeight: 600, cursor: "pointer", color: "#374151",
                    }}>
                        Cancel
                    </button>
                    <button
                        onClick={handleAssign}
                        disabled={saving || !selectedProject}
                        style={{
                            flex: 2, padding: "10px 14px",
                            borderRadius: 10, border: "none",
                            background: saving || !selectedProject ? "#e5e7eb" : "#111827",
                            color: saving || !selectedProject ? "#9ca3af" : "#fff",
                            fontSize: 13, fontWeight: 600,
                            cursor: saving || !selectedProject ? "not-allowed" : "pointer",
                            transition: "all 0.15s",
                        }}
                    >
                        {saving ? "Assigning..." : `Assign Project${selectedProject ? ` — ${selectedProject.project_name}` : ""}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AssignProjectPopup;